import { Component, Input, ViewChild, OnChanges, SimpleChanges } from '@angular/core';
import { GridOptions } from 'ag-grid';
import { T2CardComponent } from 'app/trip2-theme/components/t2-card/t2-card.component';
import { anchorPopupToSource } from '../../../../core/helpers/ag-grid-popup.util';
import {
  driverScheduleFilterSlicesEqual,
  DriverScheduleFilterModel,
  DriverScheduleSortModel,
  sliceScheduleGridFilterModel,
  toDriverScheduleFilterModel
} from '../../driver-schedule-filter.model';
import { DriverScheduleService } from '../../driver-schedule.service';

@Component({
  selector: 'app-schedule',
  templateUrl: './schedule.component.html',
  styleUrls: ['./schedule.component.scss']
})
export class ScheduleComponent implements OnChanges {

  @Input('sortModel') sortModel: DriverScheduleSortModel[];
  @Input('filterModel') filterModel: DriverScheduleFilterModel;
  @Input('selectedProvider') selectedProvider?: number;

  @Input('scheduleData') scheduleData;
  @Input('selected') selected = false;

  @ViewChild('createSchedule') private scheduleModal: T2CardComponent;

  timeSelected;

  scheduleGrid: GridOptions;
  private applyingExternalSort = false;
  private applyingExternalFilter = false;

  constructor(private scheduleService: DriverScheduleService) {
    this.scheduleGrid = <GridOptions>{
      enableColResize: true,
      enableSorting: true,
      enableFilter: true,
      gridAutoHeight: true,
      suppressHorizontalScroll: true,
      columnDefs: this.getColumnDef(),
      postProcessPopup: (params) => {
        if (params && params.ePopup) {
          anchorPopupToSource(
            params.ePopup as HTMLElement,
            params.eventSource as HTMLElement,
          );
        }
      },
      onGridReady: (event) => {
        event.api.sizeColumnsToFit();
        if (this.scheduleData && this.scheduleData.columns) {
          this.scheduleGrid.api.setRowData(this.scheduleData.columns);
        }
        if (this.filterModel) {
          this.setExternalFilterModel(this.filterModel);
        }
        if (this.sortModel) {
          this.applyExternalSortModel(this.sortModel);
        }
      },
      onFilterChanged: () => {
        this.onScheduleGridFilterChanged();
      },
      onSortChanged: () => {
        this.onSortChanged();
      },
      onCellDoubleClicked: (event) => {
        this.setSelected(event);
      }
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!this.scheduleGrid || !this.scheduleGrid.api) {
      return;
    }

    if (changes['scheduleData'] && this.scheduleData) {
      this.scheduleGrid.api.setRowData(this.scheduleData.columns || []);
    }

    if (changes['filterModel']) {
      this.setExternalFilterModel(this.filterModel);
    }

    if (changes['sortModel']) {
      this.applyExternalSortModel(this.sortModel || []);
    }
  }

  private applyExternalSortModel(sortModel: DriverScheduleSortModel[]) {
    this.applyingExternalSort = true;
    this.scheduleGrid.api.setSortModel(sortModel);
    setTimeout(() => {
      this.applyingExternalSort = false;
    }, 0);
  }

  onScheduleGridFilterChanged() {
    if (this.applyingExternalFilter || !this.scheduleGrid || !this.scheduleGrid.api) {
      return;
    }
    this.scheduleService.emitScheduleFilterModelChanged(
      toDriverScheduleFilterModel(this.scheduleGrid.api.getFilterModel())
    );
    this.emitScheduleRowOrder();
    this.scheduleGrid.api.hidePopupMenu();
  }

  private setExternalFilterModel(model: DriverScheduleFilterModel | null | undefined) {
    if (!this.scheduleGrid || !this.scheduleGrid.api) {
      return;
    }
    const nextSlice = sliceScheduleGridFilterModel(model);
    const currentSlice = sliceScheduleGridFilterModel(
      toDriverScheduleFilterModel(this.scheduleGrid.api.getFilterModel())
    );
    if (driverScheduleFilterSlicesEqual(currentSlice, nextSlice)) {
      return;
    }
    this.applyingExternalFilter = true;
    this.scheduleGrid.api.setFilterModel(model || null);
    setTimeout(() => {
      this.applyingExternalFilter = false;
    }, 0);
  }

  onSortChanged() {
    if (this.applyingExternalSort || !this.scheduleGrid || !this.scheduleGrid.api) {
      return;
    }
    this.emitScheduleRowOrder();
  }

  private emitScheduleRowOrder() {
    if (!this.scheduleGrid || !this.scheduleGrid.api) {
      return;
    }
    const orderedDriverIds: number[] = [];
    this.scheduleGrid.api.forEachNodeAfterFilterAndSort((node) => {
      if (node && node.data) {
        orderedDriverIds.push(node.data.driverid);
      }
    });
    this.scheduleService.emitScheduleSortChanged({
      sortModel: (this.scheduleGrid.api.getSortModel() || []) as DriverScheduleSortModel[],
      orderedDriverIds: orderedDriverIds
    });
  }

  /**
   * this execute when user double click on grid
   * @param event grid event
   */
  setSelected(event) {
    this.timeSelected = event.data;
    this.scheduleModal.open();
  }

  /**
   * this execute when modal emit reload event
   */
  onReloadData() {
    this.scheduleModal.close();
    this.scheduleService.emitReload();
  }

  /**
   * this execute when user change seleted check
   * @param event ng model event
   */
  onChangeSelected(event: boolean) {
    const data = {
      date: this.scheduleData.date,
      selected: event
    }

    this.scheduleService.emitDateSelection(data);
  }

  /**
   * this return columns to grid
   */
  private getColumnDef() {
    const orderedSetFilterParams = this.getOrderedSetFilterParams();

    return [
      {
        headerName: 'Veh',
        field: 'vehicle',
        colId: 'vehicle',
        width: 65,
        filter: 'agSetColumnFilter',
        filterParams: orderedSetFilterParams
      },
      {
        headerName: 'Start',
        field: 'starttime',
        colId: 'starttime',
        width: 65,
        filter: 'agSetColumnFilter',
        filterParams: orderedSetFilterParams
      },
      {
        headerName: 'End',
        field: 'endtime',
        colId: 'endtime',
        width: 65,
        filter: 'agSetColumnFilter',
        filterParams: orderedSetFilterParams
      },
      {
        headerName: 'Driver Id',
        field: 'driverid',
        colId: 'driverid',
        hide: true,
        suppressToolPanel: true,
        filter: 'agSetColumnFilter',
        filterParams: orderedSetFilterParams
      },
      {
        headerName: 'Driver',
        field: 'driver',
        colId: 'driver',
        hide: true,
        suppressToolPanel: true,
        filter: 'agSetColumnFilter',
        filterParams: orderedSetFilterParams
      }
    ]
  }

  private getOrderedSetFilterParams(): { suppressSorting: boolean; comparator: (a: string, b: string) => number; cellHeight: number } {
    return {
      suppressSorting: false,
      comparator: (a: string, b: string) => this.compareFilterValues(a, b),
      cellHeight: 32
    };
  }

  private compareFilterValues(a: string, b: string): number {
    const valueA = !a ? '' : String(a);
    const valueB = !b ? '' : String(b);
    return valueA.localeCompare(valueB, undefined, { numeric: true, sensitivity: 'base' });
  }

}



// WEBPACK FOOTER //
// ./src/app/pages/driver-schedule/components/schedule/schedule.component.ts